import ErrorBoundary from '@fs/zion-error-boundary'
import { useFeatureFlag } from '@fs/zion-flags'
import { useLocation, useQueryParams } from '@fs/zion-router'
import { useSls } from '@fs/zion-sls'
import { useInfoSheet, useOverlay, usePageWidth } from '@fs/zion-ui'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ErrorBoundaryFallback from '../../../shared/components/errorBoundaryFallback/ErrorBoundaryFallback'
import ErrorOverlay from '../../../shared/components/errorOverlay/ErrorOverlay'
import GroupImage from '../groupImageBrowser'
import FeedbackOverlay from '../../../shared/components/feedbackOverlay/FeedbackOverlay'
import { errorCollector } from '../../../shared/utils/utils'
import BrowseProviders from '../../views/imageBrowse/BrowseProviders'
import SharedUsabilla from '../../../shared/components/sharedUsabilla/SharedUsabilla'
import OverlayContextProvider from '../../../shared/providers/overlayContextProvider/OverlayContext'
import NavigationTopBar from './components/navigationTopBar/NavigationTopBar'
import ViewAnotherItemDialog from './components/viewAnotherItemDialog/ViewAnotherItemDialog'
import SideSheet from '../sideSheet/SideSheet'
import { useR2StateContext } from '../../../RIP/providers/r2Provider/R2Context'
import { headers } from '../../../shared/services/config'

/** Not logged in game plan
  1. Check if the image requires login
  2. If it does, redirect to login
  5. If not then create an anonymous session that RMS requires
  4. Don't load the image data for now.  We may do that later.
  5. If the patron is already logged in and the image doesn't require login, continue as normal
*/

export default function ExploreView({ validSession }) {
  const feedbackFlag = useFeatureFlag('rip_sharedImageViewer_feedback_RII')

  const atPageWidth = usePageWidth()
  const isSmallScreen = atPageWidth({ default: true, md: false })
  const [t] = useTranslation()
  const infoSheetOverlay = useInfoSheet({ initiallyOpen: true })
  const r2State = useR2StateContext()
  const location = useLocation()
  const imageArk = location.pathname.split('/').pop()
  const { query } = useQueryParams()

  const attachToTreeFirstPanel = useOverlay()
  const viewAnotherItemOverlay = useOverlay()
  const finishedCorrectingMetadataOverlay = useOverlay()
  const editMetadataOverlay = useOverlay()
  const groupHistoryOverlay = useOverlay()
  const [errorLoading, setErrorLoading] = useState(false)

  // Fetch SLS data for the current image so the transcript button in the nav
  // has elements available even when NamesView hasn't rendered yet.
  useSls({
    imageArks: [imageArk],
    fsUserAgentChain: headers['FS-User-Agent-Chain'],
    sort: true,
    dispatch: true,
    enableHook: r2State.shouldLoadIndexedData && query.view === 'explore',
  })

  const handleError = useCallback((err, info) => {
    console.error('well, sorry about the bad news', err, info)
    errorCollector(err, null, {
      codeId: 1001,
      status: err?.response?.status,
      responseBody: err?.response?.data,
      ...info,
    })
  }, [])

  /** Since we control the login, we shouldn't load components if the session is not valid.
   * It causes the app to reload over and over. BAD!!
   * Making api calls that return 401s will cause the infinite reload.  Just don't load the components until
   * the session is checked and valid in ViewManager ************* */
  return (
    <ErrorBoundary errorHandler={handleError} FallbackComponent={ErrorBoundaryFallback}>
      <OverlayContextProvider
        infoSheetOverlay={infoSheetOverlay}
        viewAnotherItemOverlay={viewAnotherItemOverlay}
        finishedCorrectingMetadataOverlay={finishedCorrectingMetadataOverlay}
        editMetadataOverlay={editMetadataOverlay}
        attachToTreeOverlay={attachToTreeFirstPanel}
        groupHistoryOverlay={groupHistoryOverlay}
      >
        <BrowseProviders>
          <ViewAnotherItemDialog overlay={viewAnotherItemOverlay} />
          <NavigationTopBar />
          {validSession && <GroupImage />}
          {!isSmallScreen && feedbackFlag.isOn && <FeedbackOverlay />}
          <SideSheet />
          <SharedUsabilla />
          <ErrorOverlay
            error={errorLoading}
            setError={setErrorLoading}
            title={t('ssl-loading-error.heading', 'Unable to load the data')}
            message={t(
              'ssl-loading-error.content',
              'Loading record data is taking longer than expected.  Please try again later.'
            )}
          />
        </BrowseProviders>
      </OverlayContextProvider>
    </ErrorBoundary>
  )
}
